import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { showError } from '@feathr/components';
import { setSuppressSecondaryNavOnInit, useFlags, useUser } from '@feathr/extender/state';

import { resolveDismiss, resolveNavPreference } from '../navPreference';

interface IUseNavPreferenceToggleReturn {
  /** Whether the user has dismissed the toggle */
  isDismissed: boolean;
  /** Whether the user is currently using the new navigation */
  isUsingNewNav: boolean;
  /** Handler to toggle between old and new navigation */
  handleToggle: () => Promise<void>;
  /** Handler to dismiss the toggle permanently */
  handleDismiss: (event: React.MouseEvent) => Promise<void>;
}

/**
 * Hook for managing navigation preference toggle state and handlers.
 *
 * @returns Object containing state and handlers for the toggle
 *
 * @example
 * const { isDismissed, isUsingNewNav, handleToggle, handleDismiss } = useNavPreferenceToggle();
 */
export function useNavPreferenceToggle(): IUseNavPreferenceToggleReturn {
  const { t } = useTranslation();
  const user = useUser();
  const { showNewPrimaryNav } = useFlags();

  const userNavPreference = user.getSetting('use_new_primary_nav');
  const isUsingNewNav = resolveNavPreference(userNavPreference, showNewPrimaryNav);
  const userDismissSetting = user.getSetting('dismiss_new_navigation');
  const isDismissed = resolveDismiss(userDismissSetting);

  const handleToggle = useCallback(async (): Promise<void> => {
    const newValue = !isUsingNewNav;

    // When switching TO new nav, set flag to suppress secondary nav on NavigationProvider init
    if (newValue) {
      setSuppressSecondaryNavOnInit();
    }

    user.setSetting('use_new_primary_nav', newValue);
    user.setAttributeDirty('settings');

    try {
      await user.patchDirty();
      if (user.isErrored) {
        showError(user.error, t);
      }
    } catch (error) {
      showError(error, t);
    }
  }, [isUsingNewNav, user, t]);

  const handleDismiss = useCallback(
    async (event: React.MouseEvent): Promise<void> => {
      event.stopPropagation();

      user.setSetting('dismiss_new_navigation', true);
      user.setAttributeDirty('settings');

      try {
        await user.patchDirty();
        if (user.isErrored) {
          showError(user.error, t);
        }
      } catch (error) {
        showError(error, t);
      }
    },
    [user, t],
  );

  return {
    isDismissed,
    isUsingNewNav,
    handleToggle,
    handleDismiss,
  };
}
